import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Container,
  Card,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import LockIcon from '@mui/icons-material/Lock';
import Page from '../../components/Layout/Page';
import Logo from '../../components/Layout/Logo';
import LoginForm from './LoginForm';
import AuthService from '../../services/authService';
import useStyles from './styles';
import { ReduxState } from '../../types';
import { getStudentsFromRepresentative } from '../../requests/api/students';
import { StudentStage } from '../../utils/enrollment';
import { REPRESENTATIVE, STUDENT } from '../../utils/user_types';

function LoginView() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const account = useSelector((state: ReduxState) => state.account);
  const [isCheckingRepresentative, setIsCheckingRepresentative] = useState(false);

  useEffect(() => {
    const checkAndRedirect = async () => {
      if (account?.user && AuthService.isAuthenticated()) {
        // Verificar si hay un redirect en el query string
        const params = new URLSearchParams(location.search);
        const redirectPath = params.get('redirect');

        if (redirectPath) {
          // Si hay una URL de redirect, ir ahí directamente
          history.push(decodeURIComponent(redirectPath));
          return;
        }

        // Si no hay redirect, aplicar la lógica de redirección por defecto
        const enrollmentStages = [
          StudentStage.ENROLLMENT,
          StudentStage.STUDY_PLAN,
          StudentStage.SCHEDULE,
        ];
        if (account.user.userType === STUDENT
          && enrollmentStages.includes(account.user.stage as StudentStage)) {
          const target = `/inscripcion/alumno/${account.user.userTypeId}`;
          if (history.location.pathname !== target) {
            history.push(target);
          }
        } else if (account.user.userType === STUDENT) {
          const target = `/alumnos/${account.user.userTypeId}`;
          if (history.location.pathname !== target) {
            history.push(target);
          }
        } else if (account.user.userType === REPRESENTATIVE && !isCheckingRepresentative) {
          setIsCheckingRepresentative(true);
          try {
            const { students } = await getStudentsFromRepresentative(
              account.user.userTypeId,
            );

            if (students && students.length > 0) {
              const target = `/alumnos/${students[0].studentId}`;
              if (history.location.pathname !== target) {
                history.push(target);
              }
            } else {
              const target = '/alumnos';
              if (history.location.pathname !== target) {
                history.push(target);
              }
            }
          } catch (error) {
            const target = '/alumnos';
            if (history.location.pathname !== target) {
              history.push(target);
            }
          } finally {
            setIsCheckingRepresentative(false);
          }
        } else {
          const target = '/alumnos';
          if (history.location.pathname !== target) {
            history.push(target);
          }
        }
      }
    };

    checkAndRedirect();
  }, [account.user]);

  return (
    <Page
      className={classes.root}
      title="Login"
    >
      <Container maxWidth="md">
        <Box
          mb={8}
          display="flex"
          alignItems="center"
          justifyContent='center'
        >
          <Logo className={classes.logo} />
        </Box>
        <Card className={classes.card}>
          <CardContent className={classes.content}>
            <Avatar className={classes.icon}>
              <LockIcon fontSize="large" />
            </Avatar>
            <Typography
              variant="h2"
              color="textPrimary"
            >
              <FormattedMessage
                id="login.title"
              />
            </Typography>
            <Typography
              variant="subtitle1"
              color="textPrimary"
            >
              <FormattedMessage
                id="login.subtitle"
              />
            </Typography>
            <Box mt={3}>
              <LoginForm />
            </Box>
            <Box my={2}>
              <Divider />
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}

export default LoginView;
